import React, { useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { ClipboardList, UserCheck, Mail } from "lucide-react";

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-2xl font-bold text-[#012d5c] mb-4 pb-2 border-b border-gray-100">
      {title}
    </h2>
    <div className="space-y-4 text-gray-700 leading-relaxed">{children}</div>
  </section>
);

const PatientRejoiningPolicy = () => {
  useEffect(() => {
    document.title = "Patient Rejoining Policy | Integro Clinics";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content =
      "Information for former Integro Clinics patients who wish to rejoin the clinic. Learn about our updated patient rejoining policy and rescreening process.";
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navbar />

      {/* Hero */}
      <div
        className="bg-[#012d5c] py-20"
        style={{
          backgroundImage: "linear-gradient(rgba(1,45,92,0.88),rgba(1,45,92,0.88)),url('https://images.pexels.com/photos/4031818/pexels-photo-4031818.jpeg?auto=compress&cs=tinysrgb&w=1920&h=900&fit=crop')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <span className="inline-block bg-[#4caf50] text-white text-sm font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-5">
            Patient Support
          </span>
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            Patient Rejoining Policy
          </h1>
          <p className="text-gray-300 max-w-2xl mx-auto text-lg">
            Information for former patients who wish to be considered for treatment at Integro Clinics again.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">

            {/* Summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
              <div className="bg-[#e0f2fe] rounded-xl p-5 text-center">
                <ClipboardList className="w-7 h-7 text-[#005eb8] mx-auto mb-3" />
                <p className="text-sm font-bold text-[#012d5c]">Updated Policy</p>
                <p className="text-xs text-gray-600 mt-1">Former patients may now be considered for treatment again.</p>
              </div>
              <div className="bg-[#e8f5e9] rounded-xl p-5 text-center">
                <ClipboardList className="w-7 h-7 text-[#4caf50] mx-auto mb-3" />
                <p className="text-sm font-bold text-[#012d5c]">SCR Required</p>
                <p className="text-xs text-gray-600 mt-1">An up-to-date Summary Care Record is mandatory before screening.</p>
              </div>
              <div className="bg-[#f0f4f8] rounded-xl p-5 text-center">
                <UserCheck className="w-7 h-7 text-[#2ba78c] mx-auto mb-3" />
                <p className="text-sm font-bold text-[#012d5c]">Case-by-Case Review</p>
                <p className="text-xs text-gray-600 mt-1">Each application is assessed individually by our clinical team.</p>
              </div>
            </div>

            <Section title="Our Commitment to Patients">
              <p>
                At Integro Clinics, we are committed to providing safe, professional, and patient-focused care. We continually review our policies to ensure they reflect best practice and support the needs of our patients.
              </p>
              <p>
                We have recently updated our policy regarding patients who previously chose to leave the clinic for financial reasons. Under our updated policy, former patients may now be considered for treatment again, subject to a new clinical screening process.
              </p>
            </Section>

            <Section title="How the Rejoining Process Works">
              <p>
                To begin the process, patients must provide their latest Summary Care Record (SCR). An up-to-date SCR is mandatory before any screening or assessment can take place.
              </p>
              <p>
                Once we receive and review the SCR, our clinical team will assess whether the patient meets our current eligibility criteria and whether treatment is clinically appropriate.
              </p>
              <p>
                This change allows us to review each case individually while continuing to prioritise patient safety and clinical standards.
              </p>
            </Section>

            <Section title="Important Notice">
              <p>
                Please note that patients who were discharged for clinical or safety-related reasons may still not be eligible to rejoin the clinic. Our clinical team will make this determination during the review process.
              </p>
              <p>
                Submitting a request to rejoin does not guarantee acceptance. All decisions are made solely on clinical grounds and in the best interest of patient safety.
              </p>
            </Section>

            <Section title="Contact Us">
              <p>
                For more information or to start the rescreening process, please contact us via the website or email us directly.
              </p>
              <div className="mt-4 flex items-center gap-3 bg-[#f0f4f8] rounded-xl px-5 py-4 inline-flex">
                <Mail className="w-5 h-5 text-[#4caf50] shrink-0" />
                <a
                  href="mailto:office@integroclinics.com"
                  className="text-[#005eb8] font-semibold hover:underline text-sm"
                >
                  office@integroclinics.com
                </a>
              </div>
            </Section>

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PatientRejoiningPolicy;
